import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../utils/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check admin authentication
    const isAdmin = localStorage.getItem('isAdmin');
    const adminToken = localStorage.getItem('adminToken');
    
    if (!isAdmin || !adminToken) {
      navigate('/admin-login');
      return;
    }

    // Fetch users and documents
    const fetchData = async () => {
      try {
        setLoading(true);
        const [usersData, documentsData] = await Promise.all([
          adminAPI.getAllUsers(),
          adminAPI.getAllDocuments()
        ]);
        setUsers(usersData.users || []);
        setDocuments(documentsData.documents || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load data');
        console.error('Error fetching admin data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminEmail');
    navigate('/admin-login');
  };

  // Group documents by user
  const getDocumentsByUser = (userId) => {
    return documents.filter(doc => doc.user_id === userId);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Dashboard</h1>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <main className="admin-main">
        {error && <div className="error-banner">{error}</div>}
        
        <div className="stats-section">
          <div className="stat-card">
            <div className="stat-value">{users.length}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{documents.length}</div>
            <div className="stat-label">Total Documents</div>
          </div>
        </div>

        <div className="users-section">
          <h2>Users</h2>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Date of Joining</th>
                  <th>Documents</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">No users found</td>
                  </tr>
                ) : (
                  users.map((user) => {
                    const userDocuments = getDocumentsByUser(user.id);
                    return (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`plan-badge plan-${user.plan}`}>
                            {user.plan.toUpperCase()}
                          </span>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>{userDocuments.length}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="documents-section">
          <h2>All Documents</h2>
          <div className="documents-grid">
            {documents.length === 0 ? (
              <div className="no-data">No documents found</div>
            ) : (
              documents.map((doc) => {
                const user = users.find(u => u.id === doc.user_id);
                return (
                  <div key={doc.id} className="document-card">
                    <div className="document-header">
                      <h3>{doc.filename}</h3>
                      <span className={`status-badge status-${doc.status}`}>
                        {doc.status}
                      </span>
                    </div>
                    <div className="document-info">
                      <p><strong>User:</strong> {user ? user.name : `ID: ${doc.user_id}`}</p>
                      <p><strong>Email:</strong> {user ? user.email : 'N/A'}</p>
                      <p><strong>Size:</strong> {(doc.file_size / 1024).toFixed(2)} KB</p>
                      <p><strong>Uploaded:</strong> {formatDate(doc.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
